import { FormLabel, Grid } from '@mui/material';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  SetupIntentResult,
  Stripe,
  StripeCardNumberElementChangeEvent,
} from '@stripe/stripe-js';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { loadStripe } from 'src/models/cart/utils';

import { TextField } from './TextField';
import { Typography } from './Typography';

export interface PaymentMethodSplitFormRef {
  submit: () => Promise<SetupIntentResult | undefined>;
}

interface PaymentMethodSplitFormProps {
  clientSecret: string;
  onCardNumberChange: (e: StripeCardNumberElementChangeEvent) => void;
  stackFieldsVertically?: boolean;
}

const PaymentMethodSplitForm = forwardRef<
  PaymentMethodSplitFormRef,
  PaymentMethodSplitFormProps
>(({ clientSecret, onCardNumberChange, stackFieldsVertically }, ref) => {
  const { t } = useTranslation('translation', { keyPrefix: 'paymentMethod' });
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const { session, sessionLoading } = useOptionalSession();

  useEffect(() => {
    if (session && !stripePromise) {
      setStripePromise(loadStripe(session));
    }
  }, [session, stripePromise]);

  if (!clientSecret) {
    return <div>{t('loading')}</div>;
  }

  if (!session && sessionLoading) {
    return <div>{t('loading')}</div>;
  }

  if (!session) {
    return <Typography color="error">{t('loading')}</Typography>;
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <PaymentMethodSplitFormContent
        ref={ref}
        clientSecret={clientSecret}
        onCardNumberChange={onCardNumberChange}
        stackFieldsVertically={stackFieldsVertically}
      />
    </Elements>
  );
});

PaymentMethodSplitForm.displayName = 'PaymentMethodSplitForm';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const gridSize = {
  xs: 12,
  sm: 6,
  md: 4,
};
const wrapperStyle = {
  border: '1px solid #ccc',
  padding: '10px',
  borderRadius: '8px',
  marginTop: '4px',
};

const PaymentMethodSplitFormContent = forwardRef<
  PaymentMethodSplitFormRef,
  PaymentMethodSplitFormProps
>(({ clientSecret, onCardNumberChange, stackFieldsVertically }, ref) => {
  const mainSize = stackFieldsVertically ? 12 : gridSize;
  const cvcSize = stackFieldsVertically ? 12 : 6;
  const zipSize = stackFieldsVertically ? 12 : 6;
  const stripe = useStripe();
  const elements = useElements();
  const [cardNumberEmpty, setCardNumberEmpty] = useState({
    empty: true,
    complete: false,
  });
  const [cardExpiryEmpty, setCardExpiryEmpty] = useState({
    empty: true,
    complete: false,
  });
  const [cardCvcEmpty, setCardCvcEmpty] = useState({
    empty: true,
    complete: false,
  });
  const [zip, setZip] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const [error, setError] = useState<string | null>(null);

  useImperativeHandle(ref, () => ({
    async submit() {
      setError(null);

      if (!stripe || !elements) {
        return;
      }

      const allEmpty =
        cardNumberEmpty.empty &&
        cardExpiryEmpty.empty &&
        cardCvcEmpty.empty &&
        !zip;

      if (allEmpty) {
        return;
      }

      if (
        cardNumberEmpty.complete &&
        cardExpiryEmpty.complete &&
        cardCvcEmpty.complete &&
        !zip
      ) {
        setError('Your postal code is incomplete.');
        return {
          error: {
            type: 'validation_error',
            message: 'Your postal code is incomplete.',
            code: 'incomplete_postal_code',
          },
        } as SetupIntentResult;
      }

      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement)!,
          billing_details: {
            address: { postal_code: zip },
          },
        },
      });

      if (result.error) {
        if (result.error.setup_intent?.status === 'succeeded') {
          return { setupIntent: result.error.setup_intent };
        }
        setError(result.error.message || 'Payment failed');
        return { error: result.error };
      } else {
        setError(null);
        return { setupIntent: result.setupIntent };
      }
    },
  }));

  return (
    <form ref={formRef}>
      {error && <Typography color="error">{error}</Typography>}
      <Grid container spacing={2}>
        <Grid size={mainSize}>
          <FormLabel>{'Card Number'}</FormLabel>
          <Box sx={wrapperStyle}>
            <CardNumberElement
              options={CARD_ELEMENT_OPTIONS}
              onChange={(e) => {
                setCardNumberEmpty({
                  empty: e.empty,
                  complete: e.complete,
                });
                onCardNumberChange(e);
              }}
            />
          </Box>
        </Grid>

        <Grid size={mainSize}>
          <FormLabel>{'Expiration'}</FormLabel>
          <Box sx={wrapperStyle}>
            <CardExpiryElement
              options={CARD_ELEMENT_OPTIONS}
              onChange={(e) =>
                setCardExpiryEmpty({
                  empty: e.empty,
                  complete: e.complete,
                })
              }
            />
          </Box>
        </Grid>

        <Grid size={mainSize} container spacing={2}>
          <Grid size={cvcSize}>
            <FormLabel>CVC</FormLabel>
            <Box sx={wrapperStyle}>
              <CardCvcElement
                options={CARD_ELEMENT_OPTIONS}
                onChange={(e) =>
                  setCardCvcEmpty({
                    empty: e.empty,
                    complete: e.complete,
                  })
                }
              />
            </Box>
          </Grid>

          <Grid size={zipSize}>
            <TextField
              label="Card Zip Code"
              variant="outlined"
              fullWidth
              value={zip}
              placeholder="90210"
              InputProps={{ inputProps: { maxLength: 15 } }}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value)) {
                  setZip(value);
                }
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
});

PaymentMethodSplitFormContent.displayName = 'PaymentMethodSplitFormContent';

export default PaymentMethodSplitForm;
